import { intl, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { useSelector } from 'app-redux'
import { TrainingRoomPhase } from 'common/types'
import { redux as scriptRedux } from 'features/script'
import { useQuestions, VotingResultsModal } from 'features/questions'
import { FC, ReactNode } from 'react'
import { AUTO_HIDE_MENU_RESERVED_HEIGHT } from 'room/common/presentation/auto-hide-menu-bar'
import { InPersonLayout } from 'room/common/presentation/video-layout/in-person-layout'
import { roomEndUsers } from 'room/common/redux'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { SceneVideoUser } from 'video-conference-media'
import { InPersonWelcome } from './in-person-welcome'
import { selectFocusedFeatureType } from './redux'
import { TrainerFocusedFeature } from './trainer-focused-feature'

/**
 * Nobody publishes anything in a stationary interaction scheme, so the stage
 * components get a stable empty array rather than a new one every render.
 */
const NO_STREAMS: SceneVideoUser[] = []

/**
 * `position: relative` makes the stage the containing block for the
 * auto-hiding menu, which overlays it rather than sitting below it. With a
 * menu, the bottom padding is the menu's hot zone: nothing on the stage may
 * sit under it, or the zone takes the click. `min-width: 0` lets a slide
 * shrink beside the presenter panel instead of pushing it out.
 */
const Stage = styled.div<{ withMenu: boolean }>`
	position: relative;
	display: flex;
	flex: 1;
	min-height: 0;
	min-width: 0;
	width: 100%;
	padding-bottom: ${({ withMenu }) =>
		withMenu ? AUTO_HIDE_MENU_RESERVED_HEIGHT : 0}px;
`

type Props = {
	/** The trainer's auto-hiding menu; the projector has none. */
	menu?: ReactNode
	'data-testid'?: string
}

/**
 * The in-person training room's stage: the focused tool, or the voting
 * results while a question is open - and, before the agenda starts, the
 * welcome card rather than a blank stage. One component for the trainer's
 * room and the projector of presenter mode, so the two always show the same
 * thing.
 */
export const InPersonStage: FC<Props> = ({ menu, 'data-testid': testId }) => {
	const questions = useQuestions()
	const roomAddress = useAssertedJoinedRoomAddress()
	const trainingRoomPhase = useSelector(state => state.room.trainingRoomPhase)
	const focusedFeature = useSelector(selectFocusedFeatureType)
	const scriptStarted = useSelector(scriptRedux.selectScriptStarted)
	const endUsers = useSelector(roomEndUsers)

	/**
	 * `SELECTING_GROUPS` and `REVIEWING_TASKS` belong to the real `Grouping`
	 * feature, which a stationary interaction scheme does not enable, so
	 * `INITIAL` is the only phase this room can be in. Anything else means the
	 * room is in a state we have no screen for, and saying so beats rendering a
	 * blank projector.
	 */
	if (trainingRoomPhase !== TrainingRoomPhase.INITIAL) {
		return (
			<Stage withMenu={!!menu}>
				<InPersonLayout>
					<ui.ErrorInline>
						<intl.Translate>in_person.room.unexpected_phase</intl.Translate>
					</ui.ErrorInline>
				</InPersonLayout>
			</Stage>
		)
	}

	return (
		<Stage withMenu={!!menu} data-testid={testId}>
			<InPersonLayout>
				{focusedFeature ? (
					<TrainerFocusedFeature streams={NO_STREAMS} />
				) : scriptStarted ? null : (
					<InPersonWelcome />
				)}
			</InPersonLayout>
			{questions.active && !focusedFeature ? (
				<VotingResultsModal
					roomAddress={roomAddress}
					totalNumberOfUsers={endUsers.length}
				/>
			) : null}
			{menu}
		</Stage>
	)
}

export default InPersonStage
